import type { ComponentType } from 'react';

import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

export enum MenuItemVisibility {
  SITTER = 'SITTER',
  OWNER = 'OWNER',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  BRIGHT_HORIZONS = 'BRIGHT_HORIZONS',
}

export type MenuItemType = {
  path: string;
  displayName: string;
  visibility: MenuItemVisibility[];
  icon?: ComponentType;
  isBHCompatible?: boolean;
};

/*
 * `getShouldShowItem` takes an array of MenuItemVisibility and determines
 * if any of the predefined tests in `menuItemVisibilityTestMap` pass, given
 * values of isAuthenticated, isActiveServiceProvider. If at least one test passes,
 * we want to show the item.
 *
 * Example: getShouldShowItem(
 *  [MenuItemVisibility.SITTER, MenuItemVisibility.UNAUTHENTICATED]
 *  true,
 *  false,
 * )
 *
 * This would check the tests for MenuItemVisibility.SITTER and MenuItemVisibility.UNAUTHENTICATED.
 * Given these props, we would end up with an array like [false, false], so the function would
 * return false.
 */
export const getShouldShowItem = (
  visibility: MenuItemVisibility[],
  isAuthenticated: boolean,
  isActiveServiceProvider: boolean,
  isBrightHorizonsNavigation?: boolean,
  isBHCompatible?: boolean
): boolean => {
  const menuItemVisibilityTestMap = {
    [MenuItemVisibility.SITTER]: isActiveServiceProvider,
    [MenuItemVisibility.OWNER]: isAuthenticated && !isActiveServiceProvider,
    [MenuItemVisibility.UNAUTHENTICATED]: !isAuthenticated,
  };
  const doesAtLeastOneTestReturnTruthy = Object.values(
    Object.keys(menuItemVisibilityTestMap)
      .filter((key) => visibility.includes(key as MenuItemVisibility))
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: menuItemVisibilityTestMap[key] });
      }, {})
  ).some((test) => test);

  if (isBrightHorizonsNavigation) {
    return !!isBHCompatible && doesAtLeastOneTestReturnTruthy;
  }
  if (visibility.includes(MenuItemVisibility.BRIGHT_HORIZONS)) {
    return false;
  }
  return doesAtLeastOneTestReturnTruthy;
};

export const fireEventForClickingOurServices = (): void => {
  try {
    fireDataLayerEvent({
      event: 'navigation-our-services',
    });
  } catch (error) {
    captureError(error as Error);
  }
};

/* stops propagation so we can't bubble up to the
 * document click handler that closes the dropdown
 */
export const dropdownClickHandler = (e: React.SyntheticEvent<HTMLElement>): void => {
  e.stopPropagation();
  // https://stackoverflow.com/questions/24415631/reactjs-syntheticevent-stoppropagation-only-works-with-react-events
  e.nativeEvent.stopImmediatePropagation();
};
